import { Injectable } from '@angular/core';
import { AuthService } from '@auth0/auth0-angular';
import { Observable } from 'rxjs';
import { AttributionService } from '@core/services/marketing/attribution.service';
import { SessionService } from '@core/services/session/session.service';

export interface LoginRedirectOptions {
  /**
   * Where to send the user after authentication. Carried in Auth0 `appState`,
   * which is the only mechanism used for route restoration — marketing params
   * never take part in it.
   */
  redirect: string;
  /** Extra /authorize parameters, e.g. `{ screen_hint: 'signup' }`. */
  authorizationParams?: Record<string, any>;
}

/**
 * Single entry point for every interactive login redirect (CS-3375).
 *
 * Before this existed, five call sites each hand-rolled the same
 * `loginWithRedirect({ openUrl, appState })` block, so any authorization
 * parameter had to be repeated five times. It now lives here, which is what
 * lets first-touch marketing attribution ride along on every login path
 * (guard-triggered, /login, /signup, navbar, product detail) without changing
 * their individual behaviour.
 *
 * `openUrl` keeps the existing semantics exactly: flush the cached user/client
 * /decorator session, then hard-replace the location. Note that
 * `SessionService.flushUser()` intentionally does not touch the attribution key.
 */
@Injectable({ providedIn: 'root' })
export class LoginRedirectService {
  constructor(
    private auth: AuthService,
    private session: SessionService,
    private attribution: AttributionService
  ) {}

  public loginWithRedirect(options: LoginRedirectOptions): Observable<void> {
    const marketingParams = this.attribution.toAuthorizationParams();
    const authorizationParams = {
      ...(options.authorizationParams ?? {}),
      ...marketingParams,
    };

    return this.auth.loginWithRedirect({
      // Omit the key entirely when there is nothing to add, so a user who
      // arrived without campaign params gets the pre-existing request.
      ...(Object.keys(authorizationParams).length > 0 ? { authorizationParams } : {}),
      openUrl: (url) => {
        this.session.flushUser();
        window.location.replace(url);
      },
      appState: {
        redirect: options.redirect,
      },
    });
  }
}
